import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Send, X, Loader2, Bot, User } from 'lucide-react';
import { sendChatToBot } from '../services/n8nService';

const ChatWidget: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<{ role: 'user' | 'bot'; text: string }[]>([
    { role: 'bot', text: 'Guten Tag! Ich bin ARKO – der KI-Assistent von ARKORBIT AI Austria. Wie kann ich Ihnen helfen?' }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-Teaser nach 3 Sekunden zeigen
  useEffect(() => {
    const timer = setTimeout(() => setShowTeaser(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  // Auto-Scroll zum Ende
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, isLoading]);

  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: userMessage }]);
    setIsLoading(true);
    setShowTeaser(false);

    try {
      const response = await sendChatToBot(userMessage, messages);
      setMessages(prev => [...prev, { role: 'bot', text: response }]);
    } catch (error) {
      setMessages(prev => [...prev, { role: 'bot', text: 'Verbindung zum Orbit-Knoten unterbrochen. Bitte versuchen Sie es später erneut.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed bottom-8 left-8 z-[100] font-sans">
      {/* Teaser Bubble */}
      {showTeaser && !isOpen && (
        <div className="absolute bottom-20 left-0 w-64 bg-slate-900 border border-blue-500/30 p-4 rounded-2xl shadow-2xl animate-fade-in-up backdrop-blur-xl">
          <p className="text-xs text-slate-300">
            <span className="text-blue-400 font-bold">ARKO:</span> Darf ich Ihnen zeigen, wie viel Zeit Ihre Kanzlei monatlich verschwendet?
          </p>
          <div className="absolute -bottom-2 left-6 w-4 h-4 bg-slate-900 border-r border-b border-blue-500/30 rotate-45"></div>
        </div>
      )}

      {/* Floating Button */}
      <button
        onClick={() => { setIsOpen(!isOpen); setShowTeaser(false); }}
        className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 shadow-2xl group ${
          isOpen ? 'bg-slate-800 rotate-90 border border-white/10' : 'bg-blue-600 hover:bg-blue-500 border border-white/20'
        }`}
      >
        {isOpen ? <X className="text-white w-6 h-6" /> : <MessageSquare className="text-white w-6 h-6 group-hover:scale-110 transition-transform" />}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="absolute bottom-20 left-0 w-[350px] max-w-[calc(100vw-1.5rem)] md:w-[400px] md:max-w-none h-[500px] bg-slate-950 border border-white/10 rounded-3xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex flex-col overflow-hidden animate-fade-in-up backdrop-blur-2xl">
          {/* Header */}
          <div className="bg-slate-900/50 p-4 border-b border-white/5 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600/20 rounded-xl flex items-center justify-center border border-blue-500/30 overflow-hidden flex-shrink-0 relative">
                <img src="/arkorbit-wappen.png" alt="ARKO" className="absolute inset-0 w-full h-full object-cover object-center" style={{ transform: 'scale(1.9) translateY(23%)' }} />
              </div>
              <div>
                <h4 className="text-white font-bold text-sm tracking-tight">ARKO · KI-Assistent</h4>
                <div className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-2 animate-pulse"></span>
                  <span className="text-[10px] text-slate-500 uppercase tracking-widest font-bold">Node: Frankfurt-Active</span>
                </div>
              </div>
            </div>
          </div>

          {/* Messages Area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
            {messages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user' 
                    ? 'bg-blue-600 text-white rounded-tr-none shadow-lg' 
                    : 'bg-slate-900 text-slate-300 border border-white/5 rounded-tl-none shadow-md'
                }`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start animate-pulse">
                <div className="bg-slate-900 p-3 rounded-2xl rounded-tl-none border border-white/5 flex items-center space-x-2">
                  <div className="w-1.5 h-1.5 bg-slate-600 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-slate-600 rounded-full animate-bounce delay-75"></div>
                  <div className="w-1.5 h-1.5 bg-slate-600 rounded-full animate-bounce delay-150"></div>
                </div>
              </div>
            )}
          </div>

          {/* Input Area */}
          <form onSubmit={handleSend} className="p-4 bg-slate-900/30 border-t border-white/5">
            <div className="relative flex items-center">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ihre Nachricht..."
                className="w-full bg-slate-950 border border-slate-800 rounded-xl py-3 pl-4 pr-12 text-sm text-white focus:outline-none focus:border-blue-500 transition-colors placeholder-slate-600"
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="absolute right-2 p-2 text-blue-500 hover:text-blue-400 disabled:text-slate-700 transition-colors"
              >
                {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
            <p className="text-[9px] text-center text-slate-600 mt-3 uppercase tracking-widest">
              Verschlüsselt via RSA-4096 & TLS 1.3
            </p>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatWidget;